import React, { useState, useRef, useEffect } from 'react';
import {
  Menu, X, CheckCircle2, Shield, Smartphone,
  BarChart3, Zap, IndianRupee, ArrowRight, Star,
  Download, Instagram, Twitter, Mail, MousePointerClick
} from 'lucide-react';
import { motion, useScroll, useTransform, useSpring, useMotionValue, Variants } from 'framer-motion';
import Button from './components/Button';
import PhoneMockup from './components/PhoneMockup';
import { Feature, FaqItem } from './types';
import { useCanonical } from './hooks/useCanonical';

// Animation variants
const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Play store link of the application
const PLAY_STORE_LINK = "https://play.google.com/store/apps/details?id=com.maniactech.hisabkitabfinance";

// Helper to track GA events
const trackEvent = (eventName: string, params?: Record<string, any>) => {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', eventName, params);
  }
};

function LandingPage() {
  useCanonical('https://hisab-kitab.maniac-tech.com/');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  const [activePhoneView, setActivePhoneView] = useState<'dashboard' | 'analytics'>('dashboard');

  // Hero Tilt Effect Logic
  const heroRef = useRef<HTMLDivElement>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  // Smooth out the mouse values
  const springConfig = { damping: 25, stiffness: 150 };
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [10, -10]), springConfig);
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-10, 10]), springConfig);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseXPct = (e.clientX - rect.left) / width - 0.5;
    const mouseYPct = (e.clientY - rect.top) / height - 0.5;
    mouseX.set(mouseXPct);
    mouseY.set(mouseYPct);
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  // Update phone view based on hovered feature
  useEffect(() => {
    if (hoveredFeature === 1) { // Index of "Category Insights"
      setActivePhoneView('analytics');
    } else {
      setActivePhoneView('dashboard');
    }
  }, [hoveredFeature]);

  const features: Feature[] = [
    {
      title: "Lightning Fast Entry",
      description: "Log expenses in seconds. Designed for the quick 'chai-sutta' breaks or auto-rickshaw rides.",
      icon: <Zap className="w-6 h-6 text-yellow-400" />
    },
    {
      title: "Category Insights",
      description: "Understand if you're spending more on 'Food' or 'Travel' with beautiful, easy-to-read charts.",
      icon: <BarChart3 className="w-6 h-6 text-yellow-400" />
    },
    {
      title: "Indian Context First",
      description: "Built for UPI, Cash, and Card splits. We speak your financial language.",
      icon: <IndianRupee className="w-6 h-6 text-yellow-400" />
    },
    {
      title: "Private & Secure",
      description: "Your data stays on your device. We don't sell your financial habits to banks.",
      icon: <Shield className="w-6 h-6 text-zinc-400" />
    }
  ];

  const faqs: FaqItem[] = [
    {
      question: "Is my financial data safe?",
      answer: "Absolutely. Hisab Kitab prioritizes local-first storage. Your transaction data resides primarily on your device, and is sent to our servers over https. We do not sell or share your personal financial data with third-party advertisers."
    },
    {
      question: "Does it support UPI auto-tracking?",
      answer: "Currently, we focus on manual entry to ensure 100% accuracy and privacy. However, our 'SMS Reader' feature (optional permission) can help draft entries from bank SMS for you to approve."
    },
    {
      question: "Is the app free?",
      answer: "Yes! The core features—unlimited transactions, categories, and monthly summaries—are completely free. We will introduce a premium tier for advanced export and sync features later."
    },
    {
      question: "Is it available on iOS?",
      answer: "We are currently Android-first to serve the widest user base in India. The iOS version is in development and will launch later this year."
    }
  ];

  const openPlayStoreLink = () => {
    trackEvent('download_click', {
      source: 'web_landing_page'
    });
    window.open(PLAY_STORE_LINK, '_blank');
  };

  const scrollToFeatures = () => {
    trackEvent('explore_features_click');
    const element = document.getElementById('features');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const handleManifestoClick = () => {
    trackEvent('manifesto_click');
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 overflow-x-hidden">

      {/* Navbar */}
      <nav className="fixed w-full z-50 bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0 flex items-center gap-2 cursor-pointer group">
              <img src="/logo.png" alt="" width={32} height={32} className="h-8 w-8" />
              <span className="font-bold text-xl text-zinc-100 tracking-tight group-hover:text-yellow-400 transition-colors">Hisab Kitab</span>
            </div>

            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-zinc-400 hover:text-yellow-400 font-medium transition-colors">Features</a>
              <a href="#sandbox" className="text-zinc-400 hover:text-yellow-400 font-medium transition-colors">Live Demo</a>
              <a href="#faq" className="text-zinc-400 hover:text-yellow-400 font-medium transition-colors">FAQ</a>
              <a href="/blog" className="text-zinc-400 hover:text-yellow-400 font-medium transition-colors">Blog</a>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button size="sm" onClick={openPlayStoreLink}>Download App</Button>
              </motion.div>
            </div>

            <div className="md:hidden">
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-zinc-400 hover:text-yellow-400">
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            className="md:hidden bg-zinc-950 border-b border-zinc-800"
          >
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              <a href="#features" className="block px-3 py-2 rounded-md text-base font-medium text-zinc-300 hover:text-yellow-400 hover:bg-zinc-900">Features</a>
              <a href="#sandbox" className="block px-3 py-2 rounded-md text-base font-medium text-zinc-300 hover:text-yellow-400 hover:bg-zinc-900">Live Demo</a>
              <a href="#faq" className="block px-3 py-2 rounded-md text-base font-medium text-zinc-300 hover:text-yellow-400 hover:bg-zinc-900">FAQ</a>
              <a href="/blog" className="block px-3 py-2 rounded-md text-base font-medium text-zinc-300 hover:text-yellow-400 hover:bg-zinc-900">Blog</a>
              <div className="px-3 py-2">
                <Button fullWidth onClick={openPlayStoreLink}>Download App</Button>
              </div>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero Section */}
      <section
        className="pt-32 pb-16 lg:pt-40 lg:pb-24 px-4 overflow-hidden relative perspective-1000"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        ref={heroRef}
      >
        <div className="absolute top-0 right-0 -z-10 w-full h-full bg-[radial-gradient(circle_at_top_right,rgba(234,179,8,0.1),transparent)]"></div>

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center lg:text-left z-10"
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-900 border border-zinc-800 text-yellow-400 text-sm font-semibold mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-500"></span>
              </span>
              Now available on Android
            </motion.div>
            <motion.h1 variants={fadeInUp} className="text-4xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.15] mb-6">
              Smart finance tracking for <span className="text-yellow-400">modern India.</span>
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg text-zinc-400 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
              Track daily UPI spends, manage monthly budgets, and grow your savings. Dark mode by default, no ads, no clutter.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button size="lg" className="flex items-center gap-2 group w-full sm:w-auto" onClick={openPlayStoreLink}>
                  <Download size={20} />
                  Download for Android
                </Button>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button size="lg" variant="secondary" className="flex items-center gap-2 w-full sm:w-auto" onClick={scrollToFeatures}>
                  Explore Features
                  <ArrowRight size={18} />
                </Button>
              </motion.div>
            </motion.div>
            {
              //   <motion.p variants={fadeInUp} className="mt-6 text-sm text-zinc-500 font-medium">
              //   ★ 4.8/5 Rating from 10,000+ early users
              // </motion.p>
            }
          </motion.div>

          {/* Hero Visual - Interactive 3D Tilt */}
          <motion.div
            className="relative flex justify-center lg:justify-end"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            style={{
              rotateX: rotateX,
              rotateY: rotateY,
              transformStyle: "preserve-3d"
            }}
          >
            <div className="relative z-10" style={{ transform: "translateZ(50px)" }}>
              {/* Pass interactive=false for the hero to keep it simple */}
              <PhoneMockup view="dashboard" interactive={false} />
            </div>
            {/* Decorative blobs */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-yellow-500/5 rounded-full blur-3xl -z-10"></div>
          </motion.div>
        </div>
      </section>

      {/* Problem / Solution Section */}
      <section className="py-20 bg-zinc-900 border-y border-zinc-800">
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.h2 variants={fadeInUp} className="text-3xl font-bold text-white mb-4">Stop guessing where your salary went.</motion.h2>
            <motion.p variants={fadeInUp} className="text-zinc-400 text-lg">
              Between multiple UPI apps, credit card bills, and cash expenses, it’s easy to lose track. Hisab Kitab brings it all under one roof.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: <Smartphone size={24} />, title: "The UPI Clutter", desc: "Dozens of small transactions on PhonePe/GPay make your bank statement impossible to read. We clean that up.", color: "text-red-400" },
              { icon: <CheckCircle2 size={24} />, title: "One-Tap Logging", desc: "Log an expense in literally 3 seconds. Open app → Type amount → Done. It's faster than paying.", color: "text-yellow-400" },
              { icon: <BarChart3 size={24} />, title: "Real Growth", desc: "Spot leakage in your budget immediately. Save up for that trip or bike by fixing small daily leaks.", color: "text-emerald-400" }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                variants={fadeInUp}
                className="p-8 rounded-2xl bg-zinc-950 border border-zinc-800 hover:border-yellow-500/30 transition-all duration-300 hover:shadow-xl hover:shadow-yellow-500/5 group"
                whileHover={{ y: -5 }}
              >
                <div className={`h-12 w-12 bg-zinc-900 rounded-xl flex items-center justify-center ${item.color} mb-6 border border-zinc-800 group-hover:scale-110 transition-transform`}>
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-zinc-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Features Grid - Interactive Linking */}
      <section id="features" className="py-24 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="flex flex-col md:flex-row gap-16 items-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <div className="flex-1 space-y-10">
              <motion.div variants={fadeInUp}>
                <h2 className="text-yellow-400 font-bold uppercase tracking-wider text-sm mb-2">Core Features</h2>
                <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">Everything you need to be financially disciplined.</h3>
                <p className="text-zinc-400 text-lg">We stripped away the complexity of accounting software and kept the power you need for personal finance.</p>
              </motion.div>

              <div className="grid sm:grid-cols-2 gap-6">
                {features.map((feature, idx) => (
                  <motion.div
                    key={idx}
                    variants={fadeInUp}
                    className={`flex gap-4 p-4 rounded-xl transition-colors cursor-default ${hoveredFeature === idx ? 'bg-zinc-900 border border-zinc-800' : 'border border-transparent'}`}
                    onMouseEnter={() => setHoveredFeature(idx)}
                    onMouseLeave={() => setHoveredFeature(null)}
                  >
                    <div className="flex-shrink-0 mt-1">
                      <div className={`p-2 rounded-lg border transition-colors ${hoveredFeature === idx ? 'bg-zinc-800 border-yellow-500/30' : 'bg-zinc-900 border-zinc-800'}`}>
                        {feature.icon}
                      </div>
                    </div>
                    <div>
                      <h4 className={`font-bold mb-1 transition-colors ${hoveredFeature === idx ? 'text-yellow-400' : 'text-zinc-100'}`}>{feature.title}</h4>
                      <p className="text-sm text-zinc-400 leading-relaxed">{feature.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Visual Side */}
            <motion.div
              className="flex-1 flex justify-center md:justify-end relative"
              variants={fadeInUp}
            >
              <div className="relative">
                {/* Background Card */}
                <div className="absolute top-10 -left-10 w-full h-full bg-zinc-900 rounded-[2.5rem] transform rotate-[-6deg] border border-zinc-800"></div>
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
                >
                  <PhoneMockup view={activePhoneView} interactive={false} />
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* --- NEW SECTION: LIVE SANDBOX --- */}
      <section id="sandbox" className="py-24 bg-zinc-900 overflow-hidden relative">
        <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 flex justify-center">
              <div className="relative">
                {/* Pulsing glow behind phone */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] bg-yellow-400/20 rounded-full blur-[100px] animate-pulse"></div>
                <PhoneMockup interactive={true} />

                <div className="absolute -right-8 top-1/3 hidden lg:block">
                  <div className="flex items-center gap-2 bg-zinc-800/80 backdrop-blur text-xs font-bold px-3 py-1.5 rounded-full border border-zinc-700 animate-bounce text-yellow-400">
                    <MousePointerClick size={14} /> Try clicking me!
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 text-center lg:text-left">
              <div className="inline-block px-3 py-1 bg-yellow-500/10 border border-yellow-500/20 rounded-full text-yellow-400 text-xs font-bold mb-4 uppercase tracking-widest">
                Live Playground
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Experience the speed right now.</h2>
              <p className="text-zinc-400 text-lg mb-8 max-w-lg mx-auto lg:mx-0">
                No download required. Interact with the phone to see how quickly you can log an expense with our custom keypad.
              </p>

              <div className="space-y-4 max-w-sm mx-auto lg:mx-0 text-left">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-yellow-400 shrink-0 font-bold text-xs border border-zinc-700">1</div>
                  <p className="text-sm text-zinc-300"><strong className="text-white">Click the + button</strong> to open the expense logger.</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-yellow-400 shrink-0 font-bold text-xs border border-zinc-700">2</div>
                  <p className="text-sm text-zinc-300"><strong className="text-white">Tap the keypad</strong> to enter an amount (e.g. 50).</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center text-yellow-400 shrink-0 font-bold text-xs border border-zinc-700">3</div>
                  <p className="text-sm text-zinc-300"><strong className="text-white">Hit Save</strong> and watch your balance update instantly.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="how-it-works" className="py-24 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl font-bold text-white mb-16"
          >
            Master your money in 3 steps
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-12 relative">
            {/* Connector Line (Desktop) */}
            <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-0.5 bg-zinc-800 -z-10"></div>

            {[
              { title: "Download & Setup", desc: "No long signups. Just set your currency and monthly budget limit.", step: "01" },
              { title: "Log Daily", desc: "Spend 1 minute a day logging your UPI and cash spends.", step: "02" },
              { title: "Understand", desc: "Check the dashboard weekly to see if you are staying within budget.", step: "03" }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                className="relative bg-zinc-950 pt-4"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.2 }}
              >
                <div className="w-16 h-16 mx-auto bg-zinc-800 text-yellow-400 border border-zinc-700 rounded-2xl flex items-center justify-center font-bold text-xl shadow-lg shadow-black/20 mb-6 z-10 relative group hover:border-yellow-400 transition-colors">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-zinc-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      {/* <section className="py-20 bg-black text-white overflow-hidden relative border-y border-zinc-900">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-zinc-900 via-zinc-950 to-black"></div>
        <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-12"
          >
            Trusted by smart spenders across India
          </motion.h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { name: "Priya Sharma", role: "Product Manager, Bangalore", text: "Finally an app that doesn't feel like a spreadsheet. The dark mode is beautiful and easy on the eyes." },
              { name: "Arjun Mehta", role: "Freelancer, Delhi", text: "The categories help me separate my business expenses from personal ones perfectly. A must-have." },
              { name: "Sneha Reddy", role: "Student, Hyderabad", text: "I used to be broke by the 20th of every month. Hisab Kitab showed me I was spending 5k just on coffee!" }
            ].map((review, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-zinc-900/50 backdrop-blur-sm p-6 rounded-2xl border border-zinc-800 text-left hover:border-yellow-500/30 transition-colors hover:bg-zinc-900"
              >
                <div className="flex gap-1 mb-4 text-yellow-400">
                  {[1, 2, 3, 4, 5].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
                </div>
                <p className="text-zinc-300 mb-6 leading-relaxed">"{review.text}"</p>
                <div>
                  <p className="font-bold text-white">{review.name}</p>
                  <p className="text-xs text-zinc-500">{review.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* FAQ */}
      <section id="faq" className="py-24 bg-zinc-950">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: idx * 0.1 }}
                className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden"
              >
                <button
                  className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none hover:bg-zinc-800/50 transition-colors"
                  onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                >
                  <span className="font-semibold text-zinc-200">{faq.question}</span>
                  <span className={`transform transition-transform duration-200 ${activeFaq === idx ? 'rotate-180' : ''}`}>
                    <ArrowRight className="w-5 h-5 text-zinc-500 rotate-90" />
                  </span>
                </button>
                {activeFaq === idx && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="px-6 pb-4 text-zinc-400 text-sm leading-relaxed border-t border-zinc-800 pt-4"
                  >
                    {faq.answer}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-20 bg-zinc-900">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-zinc-800 rounded-[2.5rem] p-8 md:p-16 text-center text-white relative overflow-hidden shadow-2xl shadow-black/50 border border-zinc-700"
          >
            {/* Background pattern */}
            <div className="absolute top-0 left-0 w-full h-full opacity-5 bg-[radial-gradient(circle_at_center,#facc15,transparent)] scale-150"></div>

            <h2 className="text-3xl md:text-5xl font-bold mb-6 relative z-10">Start your financial journey today.</h2>
            <p className="text-zinc-300 text-lg mb-10 max-w-2xl mx-auto relative z-10">
              Join Indian users who have taken control of their financial future.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center relative z-10">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-yellow-400 text-zinc-950 font-bold px-8 py-4 rounded-xl hover:bg-yellow-500 transition-colors shadow-lg shadow-yellow-400/20 flex items-center justify-center gap-2"
                onClick={openPlayStoreLink}
              >
                <Download size={20} />
                Get it on Play Store
              </motion.button>
              {/* <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-zinc-900 text-white font-semibold px-8 py-4 rounded-xl hover:bg-zinc-950 transition-colors border border-zinc-700"
                onClick={handleManifestoClick}
              >
                Read our Manifesto
              </motion.button> */}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer Links */}
      <footer className="bg-zinc-950 py-12 border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="" width={28} height={28} className="h-7 w-7" />
            <span className="font-bold text-zinc-300">Hisab Kitab</span>
          </div>

          <div className="flex gap-8 text-sm text-zinc-500">
            <a href="/blog" className="hover:text-yellow-400 transition-colors">Blog</a>
            <a href="/privacy-policy" className="hover:text-yellow-400 transition-colors">Privacy Policy</a>
          </div>

          {/* <div className="flex gap-8 text-sm text-zinc-500">
            <a href="#" className="hover:text-yellow-400 transition-colors">Privacy Policy</a>
            <a href="#" className="hover:text-yellow-400 transition-colors">Terms of Service</a>
            <a href="#" className="hover:text-yellow-400 transition-colors">Support</a>
          </div> */}

          {/* <div className="flex gap-4">
            <a href="#" className="text-zinc-500 hover:text-yellow-400 transition-colors"><Twitter size={20} /></a>
            <a href="#" className="text-zinc-500 hover:text-yellow-400 transition-colors"><Instagram size={20} /></a>
            <a href="#" className="text-zinc-500 hover:text-yellow-400 transition-colors"><Mail size={20} /></a>
          </div> */}
        </div>
        <div className="text-center mt-8 text-xs text-zinc-600">
          © {new Date().getFullYear()} Hisab Kitab. Made with ❤️ in India.
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;